import { Button } from "@/components/ui/button";
import { ArrowRight, Shield, Lock, FileCheck } from "lucide-react";

const HeroSection = ({ onGetStarted }) => {
  return (
    <section className="py-16 lg:py-24 overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Content - 7 columns */}
          <div className="lg:col-span-7 animate-fade-in-up">
            {/* Label */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFF3ED] rounded-full mb-6">
              <Shield className="w-4 h-4 text-[#C04000]" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C04000]">
                Blockchain Secured
              </span>
            </div>

            {/* Main Heading */}
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="hero-heading"
            >
              Real Estate
              <br />
              <span className="text-[#C04000]">Transactions</span>
              <br />
              Reimagined
            </h1>

            {/* Rust accent line */}
            <div className="rust-accent-line mb-6"></div>

            {/* Subtext */}
            <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl" data-testid="hero-subtext">
              Tour properties on your own schedule. Our blockchain ledger technology ensures secure title records, 
              transparent sales, and verifiable proof of ownership—all without needing a traditional real estate agent.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Button
                size="lg"
                onClick={onGetStarted}
                className="bg-[#C04000] hover:bg-[#A03500] text-white font-semibold px-8 py-6 text-lg"
                data-testid="hero-cta-button"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => document.querySelector('#blockchain')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-slate-300 text-slate-700 hover:bg-slate-50 font-semibold px-8 py-6 text-lg"
                data-testid="hero-learn-more-button"
              >
                Learn How It Works
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#C04000]" />
                <span>Credit Check Required</span>
              </div>
              <div className="flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-[#C04000]" />
                <span>Background Verified</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#C04000]" />
                <span>Secure Blockchain</span>
              </div>
            </div>
          </div>

          {/* Right Image - 5 columns */}
          <div className="lg:col-span-5 animate-fade-in-up animation-delay-200">
            <div className="relative">
              {/* Main Image */}
              <div className="rounded-lg overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/30227710/pexels-photo-30227710.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Modern architectural building"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                  data-testid="hero-image"
                />
              </div>
              
              {/* Floating Stats Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-lg shadow-xl p-5 border border-slate-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#C04000]/10 rounded-lg flex items-center justify-center">
                    <Shield className="w-6 h-6 text-[#C04000]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>10,000+</p>
                    <p className="text-sm text-slate-500">Verified Transactions</p>
                  </div>
                </div>
              </div>

              {/* Decorative Element */}
              <div className="absolute -top-4 -right-4 w-24 h-24 border-4 border-[#C04000]/20 rounded-lg -z-10"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
